import { MATCH_DEFAULT } from "@/constants";
import { firestore } from "@/services/firebase";
import { cachedTimeOffset, serverTimestamp } from "@/utils/firebase-helper";
import MatchHelper from "@/utils/match-helper";
import TokenHelper from "@/utils/token-helper";
import { GAME_OVERRIDE, MATCH_STATUS } from "vue-chess-shared/constants";
import { uuidv4 } from "vue-chess-shared/utils";
import { mapActions, mapGetters } from "vuex";

const matchesRef = firestore.collection("matches");
const openMatchesRef = firestore.collection("openMatches");

export default {
  data() {
    return {
      openMatches: [],
      liveMatches: [],
    };
  },

  watch: {
    currentMatch: {
      immediate: true,
      handler() {
        this.setCurrentMatch(this.currentMatch);
      },
    },
  },

  computed: {
    ...mapGetters({
      currentUserId: "getUserId",
      currentPlayer: "getPlayer",
    }),

    incompleteMatches() {
      return [...this.openMatches, ...this.liveMatches];
    },

    // if there is an open or live match, then watch the match and automatically back to it in a watch
    // except for the simul match because users are allowed to play other games
    // as long as they are not hosting a any simul
    currentMatch() {
      return this.incompleteMatches.find(
        (m) =>
          !m.result &&
          ((m.player1 && m.player1.user_id === this.currentUserId) ||
            (m.player2 && m.player2.user_id === this.currentUserId))
      );
    },
  },

  methods: {
    ...mapActions(["setCurrentMatch"]),

    // opponent is the player to play with in case of openning a new match
    // opponent is empty will create an open match
    // opponent is not empty will create an live match
    async joinOpenMatchOrOpenNewMatch(
      duration = MATCH_DEFAULT.DURATION,
      increment = MATCH_DEFAULT.INCREMENT,
      variant = MATCH_DEFAULT.VARIANT,
      gameOverride = MATCH_DEFAULT.GAME_OVERRIDE,
      opponent = null
    ) {
      if (await TokenHelper.checkExpiry()) return;
      if (!this.bindFirestore) return;

      if (this.currentMatch) {
        return;
      }

      // if there currently is an open match with the same preferences, then join it
      for (const match of this.openMatches) {
        if (match.duration === duration && match.variant === variant) {
          return this.joinMatch(match);
        }
      }

      // otherwise create a new preferred match and return the generated match id
      return this.openMatch(
        opponent,
        duration,
        increment,
        variant,
        gameOverride
      );
    },

    async openMatch(
      opponent,
      duration = MATCH_DEFAULT.DURATION,
      increment = MATCH_DEFAULT.INCREMENT,
      variant = MATCH_DEFAULT.VARIANT,
      gameOverride = GAME_OVERRIDE.PVP,
      simulId = null
    ) {
      const me = {
        ...this.currentPlayer,
        timeOffset: cachedTimeOffset(),
      };

      let matchId = uuidv4();
      const matchData = {
        match_id: matchId,
        duration,
        increment,
        variant,
        gameOverride,
        player1: me,
        player2: null,
        status: MATCH_STATUS.CREATED,
        createdAt: serverTimestamp(),
      };

      if (opponent) {
        // both players are present, fill the open match already
        matchData.player2 = opponent;
        matchData.status = MATCH_STATUS.STARTED;
        matchData.updatedAt = serverTimestamp();
      } else {
        // create an open match on firestore for other to join
        await openMatchesRef.doc(matchId).set(matchData);
        // An open match does not requires creating a record in mysql, so just return
        return matchId;
      }

      const switchSide = Math.random() < 0.5;
      matchId = await MatchHelper.createMatch({
        matchId: matchId,
        whitePlayer: switchSide ? opponent : me,
        blackPlayer: switchSide ? me : opponent,
        gameOverride: gameOverride,
        duration: duration,
        increment: increment,
        variant: variant,
        simulId: simulId,
      });

      if (!matchId) {
        this.$buefy.toast.open(
          "OOps! Cannot start new match. Please try again."
        );
        return;
      }

      return matchId;
    },

    async joinMatch(match) {
      if (match.player2) return;
      if (match.player1.user_id === this.currentPlayer.user_id) return;
      if (match.status !== MATCH_STATUS.CREATED) return;

      const whitePlayer = match.player1;
      const blackPlayer = {
        ...this.currentPlayer,
        timeOffset: cachedTimeOffset(),
      };

      openMatchesRef.doc(match.id).update({
        player1: whitePlayer,
        player2: blackPlayer,
        status: MATCH_STATUS.STARTED,
        updatedAt: serverTimestamp(),
      });

      const switchSide = Math.random() < 0.5;
      const matchId = await MatchHelper.createMatch({
        matchId: match.id,
        whitePlayer: switchSide ? blackPlayer : whitePlayer,
        blackPlayer: switchSide ? whitePlayer : blackPlayer,
        gameOverride: GAME_OVERRIDE.PVP,
        duration: match.duration,
        increment: match.increment,
        variant: match.variant,
      });

      if (!matchId) {
        this.$buefy.toast.open("The match is no longer available");
        return;
      }

      return matchId;
    },
  },

  async mounted() {
    await this.$bind(
      "openMatches",
      openMatchesRef.where("status", "==", "created")
    );
    await this.$bind("liveMatches", matchesRef.where("status", "==", "live"));
    this.bindFirestore = true;
  },

  async beforeDestroy() {
    this.bindFirestore = false;
  },
};
